import { API_ORIGIN, AUTH_ORIGIN, GET_ORIGIN, WWW_ORIGIN } from '../../config';

const requestToFetch = (
  method: string,
  endpoint: string,
  data?: Record<string, any>
) => {
  const res = fetch(endpoint, {
    method,
    credentials: 'include',
    headers: {
      Cookie: document.cookie,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      return response.json();
    })
    .catch((err) => {
      return err;
    });

  return res;
};

const fetchSharedQuoteToken = (token: string) => {
  return requestToFetch(
    'GET',
    `${API_ORIGIN}/v2/quotes/${token}/decode_shared_quote_token`
  );
};

const refreshAuthToken = () => {
  return requestToFetch('POST', `${AUTH_ORIGIN}/v1/user/refresh-token`);
};

const submitRegister = (data: Record<string, any>) => {
  return requestToFetch('POST', `${GET_ORIGIN}/register`, data);
};

const submitCompleteRegistration = (data: Record<string, any>) => {
  return requestToFetch('POST', `${GET_ORIGIN}/complete-registration`, data);
};

const getShareableEntities = (userId: string) => {
  return requestToFetch(
    'GET',
    `${API_ORIGIN}/v2/quotes/shareable_entities/${userId}`
  );
};

// https://github.com/xometry/xometry_app/blob/master/quoting_api/routes/quote_org_settings.py#L38-L78
const setQuoteOrgSetting = async (
  userId: string,
  orgId: string,
  type: string,
  active: boolean
): Promise<boolean | null> => {
  try {
    const result = await requestToFetch(
      'PUT',
      `${API_ORIGIN}/v2/quote-org-settings/${userId}/${orgId}/${type}`,
      { active }
    );
    if (!('active' in result)) {
      throw new Error(`Failed to set order org setting`);
    }

    active = !!result.active;
  } catch (err) {
    if ((err as any)?.status === 404) {
      return null;
    }
    throw err;
  }
  return active;
};

// https://github.com/xometry/xometry_app/blob/master/quoting_api/routes/quote_org_settings.py#L81-L94
const getQuoteOrgSetting = async (
  userId: string,
  orgId: string,
  type: string
): Promise<boolean | null> => {
  let active = false;
  try {
    const result = await requestToFetch(
      'GET',
      `${API_ORIGIN}/v2/quote-org-settings/${userId}/${orgId}/${type}`
    );
    active = result.active;
  } catch (err) {
    if ((err as any)?.status === 404) {
      return null;
    }
    throw err;
  }
  return active;
};

const getQuoteOrgSettings = async (
  userId: string,
  orgId: string
): Promise<any[] | null> => {
  const settings = [];
  try {
    const result = await requestToFetch(
      'GET',
      `${API_ORIGIN}/v2/quote-org-settings/${userId}/${orgId}`
    );
    settings.push(...result.settings);
  } catch (err) {
    if ((err as any)?.status === 404) {
      return null;
    }
    throw err;
  }
  return settings;
};

const setOrderOrgSetting = async (
  userId: string,
  orgId: string,
  type: string,
  active: boolean
): Promise<boolean | null> => {
  try {
    const result = await requestToFetch(
      'PUT',
      `${API_ORIGIN}/fulfillment/order-org-settings/${userId}/${orgId}/${type}`,
      { active }
    );
    if (result?.new_setting && !('active' in result.new_setting)) {
      throw new Error(`Failed to set order org setting`);
    }

    active = !!result.new_setting.active;
  } catch (err) {
    if ((err as any)?.status === 404) {
      return null;
    }
    throw err;
  }
  return active;
};

const getOrderOrgSettings = async (
  userId: string,
  orgId: string
): Promise<any[] | null> => {
  const settings = [];
  try {
    const result = await requestToFetch(
      'GET',
      `${API_ORIGIN}/fulfillment/order-org-settings/${userId}/${orgId}`
    );
    settings.push(...result.settings);
  } catch (err) {
    if ((err as any)?.status === 404) {
      return null;
    }
    throw err;
  }
  return settings;
};

const setToolOrgSetting = async (
  userId: string,
  orgId: string,
  type: string,
  active: boolean
): Promise<boolean | null> => {
  try {
    await requestToFetch(
      'POST',
      `${WWW_ORIGIN}/api/tools/tool-org-settings/${userId}/${orgId}/${type}/set`,
      { active, type }
    );
  } catch (err) {
    if ((err as any)?.status === 404) {
      return null;
    }
    throw err;
  }
  return active;
};

const getToolOrgSettings = async (
  userId: string,
  orgId: string
): Promise<any[] | null> => {
  const settings = [];
  try {
    const result = await requestToFetch(
      'GET',
      `${WWW_ORIGIN}/api/tools/tool-org-settings/${userId}/${orgId}/`
    );
    settings.push(...result.orgSettings);
  } catch (err) {
    if ((err as any)?.status === 404) {
      return null;
    }
    throw err;
  }
  return settings;
};

export default {
  fetchSharedQuoteToken,
  refreshAuthToken,
  submitCompleteRegistration,
  submitRegister,
  getShareableEntities,
  getQuoteOrgSetting,
  setQuoteOrgSetting,
  getQuoteOrgSettings,
  setOrderOrgSetting,
  getOrderOrgSettings,
  setToolOrgSetting,
  getToolOrgSettings,
};
